import React, { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, SquareMousePointer, Link2, Download, ArrowLeft, ChevronDown, MoreHorizontal, Images, ScanFace, Camera, Sparkles, Bell, BellOff, Cloud } from 'lucide-react';
import { albumsApi } from '../api/albums';
import { compositePhotoToBlob } from '../lib/clientCompositor';
import { authApi } from '../api/auth';
import { subscribeToAlbumStream } from '../api/sse';
import { googlePhotosApi, type SyncScope } from '../api/googlePhotos';
import { ActivityStats, Album, AlbumWithEvent, EventSummary, Photo, LikeVisibility } from '../types';
import { track, EVENTS } from '../lib/mixpanel';
import { useAuthStore } from '../store/authStore';
import { usePortalStore } from '../store/portalStore';
import { AlbumPhotoCell } from '../components/Albums/AlbumPhotoCell';
import { LiveBadge } from '../components/UI/LiveBadge';
import { AlbumPhotoViewer } from '../components/Albums/AlbumPhotoViewer';
import { AlbumBatchActionBar } from '../components/Albums/AlbumBatchActionBar';
import { LikePrivacyPrompt } from '../components/Albums/LikePrivacyPrompt';
import { MachhaCharacter } from '../lib/machhaAsset';
import { getPortalUrl } from '../lib/portal';
import { PhotoReportSheet } from '../components/Albums/PhotoReportSheet';
import { ShareSheet } from '../components/Albums/ShareSheet';
import { SharePublicLinkModal } from '../components/Albums/SharePublicLinkModal';
import { LiveSlideshowModal } from '../components/Albums/LiveSlideshowModal';
import { SlideshowSetupModal } from '../components/Albums/SlideshowSetupModal';
import { LikersModal } from '../components/Albums/LikersModal';
import { Button } from '../components/UI/Button';
import { TabBar, TabItem } from '../components/UI/TabBar';
import { Modal } from '../components/UI/Modal';
import { SelfiePrompt } from '../components/Albums/SelfiePrompt';
import { MyPhotosReviewStrip, ReviewPhoto } from '../components/Albums/MyPhotosReviewStrip';
import { CreatorControlsModal } from '../components/Albums/CreatorControlsModal';
import { NotificationOptInModal } from '../components/Albums/NotificationOptInModal';
import { GooglePhotosSyncButton } from '../components/Albums/GooglePhotosSyncButton';
import { PhotoCardSkeleton } from '../components/UI/Skeleton';
import { PhotoGrid, PHOTO_GRID_GAP, getPhotoGridCols } from '../components/shared/PhotoGrid';
import { useDevToolsDetection } from '../hooks/useDevToolsDetection';
import { usePageTitle } from '../hooks/usePageTitle';
import { CLIENT_ZIP_ENABLED, startClientZip } from '../services/download';
import styles from './AlbumViewPage.module.css';

// Google Photos logo as a TabBar-compatible icon component
function GooglePhotosIcon({ className }: { className?: string }) {
  return (
    <img
      src="https://d3ic3ju44zc444.cloudfront.net/assets/LogoImages/google-photos-6.svg"
      className={className}
      alt=""
      draggable={false}
      style={{ objectFit: 'contain' }}
    />
  );
}

// dir: 1 = forward (All → My Photos), -1 = backward (My Photos → All)
const tabSlideVariants = {
  enter: (dir: 1 | -1) => ({ x: dir * 48, opacity: 0 }),
  center:               { x: 0, opacity: 1 },
  exit:  (dir: 1 | -1) => ({ x: dir * -48, opacity: 0 }),
};
const tabSlideTransition = { type: 'tween', duration: 0.18, ease: 'easeInOut' } as const;

// TEMP: phone number opt-in modal disabled 2026-07-03 through 2026-07-05. Remove after that.
const PHONE_OPT_IN_DISABLED_UNTIL = new Date('2026-07-06T00:00:00').getTime();

interface ViewState {
  album: AlbumWithEvent | null;
  photos: Photo[];
  hasMore: boolean;
  totalPhotoCount: number;
  loading: boolean;
  loadingMore: boolean;
  error: 403 | 404 | 'unknown' | null;
  notMemberInviteToken?: string | null;
  // Set when a 403 is specifically a domain restriction — drives the
  // "sign in with your organization email" screen instead of a generic block.
  restrictedEmailDomains?: { domains: string[]; inviteToken: string | null } | null;
  coverPhotoUrl: string | null;
}

export function AlbumViewPage() {
  const { albumId } = useParams<{ albumId: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<ViewState>({
    album: null,
    photos: [],
    hasMore: false,
    totalPhotoCount: 0,
    loading: true,
    loadingMore: false,
    error: null,
    coverPhotoUrl: null,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // Google Photos sync — select-mode flow
  const [gpSyncSelectFilter, setGpSyncSelectFilter] = useState<'all' | 'my_photos' | null>(null);
  const [gpSyncJobTrigger, setGpSyncJobTrigger] = useState(0);
  const [gpSyncingSelected, setGpSyncingSelected] = useState(false);
  const [gpAutoOpenOptions, setGpAutoOpenOptions] = useState(0);
  // Lifted job state from GooglePhotosSyncButton — drives the status line below album name
  const [gpSyncJob, setGpSyncJob] = useState<import('../api/googlePhotos').SyncJob | null>(null);
  // Photo IDs already synced to Google Photos for this album
  const [gpSyncedPhotoIds, setGpSyncedPhotoIds] = useState<Set<string>>(new Set());
  // Full synced photo fetch state — needed to load objects not yet in the paginated list
  const [gpSyncedPhotosLoaded, setGpSyncedPhotosLoaded] = useState(false);
  const [gpSyncedPhotosLoading, setGpSyncedPhotosLoading] = useState(false);
  // Photo IDs that failed to sync — shown with a retry button on the cell
  const [gpFailedPhotoIds, setGpFailedPhotoIds] = useState<Set<string>>(new Set());
  // Photo IDs currently being retried (shows spinner on cell)
  const [gpRetryingPhotoIds, setGpRetryingPhotoIds] = useState<Set<string>>(new Set());
  const [descExpanded, setDescExpanded] = useState(false);
  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  // Select mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectingAll, setSelectingAll] = useState(false);
  const [batchDownloading, setBatchDownloading] = useState(false);

  // Viewer
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // Download all
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Overflow menu
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const moreTriggerRef = useRef<HTMLButtonElement>(null);
  const moreDropdownRef = useRef<HTMLDivElement>(null);
  const [moreDropdownPos, setMoreDropdownPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });

  // Per-photo metadata: likes + pin state
  const [photoMeta, setPhotoMeta] = useState<Record<string, { likeCount: number; isLikedByMe: boolean; isPinned: boolean }>>({});

  // Whether the current user is the album creator (enables pin controls)
  const [isCreator, setIsCreator] = useState(false);

  // Activity stats for the ticker
  const [activityStats, setActivityStats] = useState<ActivityStats | null>(null);

  // Photo report
  const [reportPhotoId, setReportPhotoId] = useState<string | null>(null);

  // Social sharing
  const [sharePhotoId, setSharePhotoId] = useState<string | null>(null);

  // Likers modal
  const [likersPhotoId, setLikersPhotoId] = useState<string | null>(null);

  // Live slideshow
  const [slideshowOpen, setSlideshowOpen] = useState(false);

  // Venue Display (slideshow management)
  const [slideshowSetupOpen, setSlideshowSetupOpen] = useState(false);
  const [venueMenuOpen, setVenueMenuOpen] = useState(false);
  const venueMenuRef = useRef<HTMLDivElement>(null);

  // Creator Controls — creator-only modal hosting stats + slideshow tools
  const [creatorControlsOpen, setCreatorControlsOpen] = useState(false);

  // Public share link modal — creator-only
  const [sharePublicOpen, setSharePublicOpen] = useState(false);

  usePageTitle(state.album?.name ?? undefined);

  // Notification preferences for this album membership
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notifModalOpen, setNotifModalOpen] = useState(false);
  const [notifModalContext, setNotifModalContext] = useState<'empty-album' | undefined>(undefined);
  const emptyAlbumPromptShownRef = useRef(false);

  // Google Photos connection state — null = not yet checked
  const [gpConnected, setGpConnected] = useState<boolean | null>(null);
  const [gpConnectChecking, setGpConnectChecking] = useState(false);

  // Face recognition
  const [activeTab, setActiveTab] = useState<'all' | 'my-photos' | 'gp-synced' | 'highlights'>('all');
  const tabDirectionRef = useRef<1 | -1>(1);
  const [myPhotoIds, setMyPhotoIds] = useState<Set<string>>(new Set());
  // Pinned + event-level highlights — universal photos the viewer is always
  // allowed to see (returned by getMyPhotos regardless of selfie). Folded into
  // the My Photos view only when the viewer is restricted to matched_only.
  const [viewerHighlightIds, setViewerHighlightIds] = useState<Set<string>>(new Set());
  const [myPhotosLoading, setMyPhotosLoading] = useState(false);
  const [myPhotosLoaded, setMyPhotosLoaded] = useState(false);
  const [myPhotosError, setMyPhotosError] = useState<string | null>(null);
  // Borderline "Are these you?" review candidates for the current album.
  const [reviewPhotos, setReviewPhotos] = useState<ReviewPhoto[]>([]);
  const [selfiePromptOpen, setSelfiePromptOpen] = useState(false);
  const [selfieJustUploaded, setSelfieJustUploaded] = useState(false);

  // Like privacy prompt — shown on first-ever like if user hasn't chosen yet
  const [privacyPromptOpen, setPrivacyPromptOpen] = useState(false);
  const pendingLikeRef = useRef<{ e: React.MouseEvent; photoId: string } | null>(null);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const cursorRef = useRef<string | null>(null);
  const loadingMoreRef = useRef(false);
  const newPhotosBufferRef = useRef<number>(0);
  const sseToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs that break closure dependencies so callbacks can remain stable
  // while still reading the latest value on each invocation.
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);
  const photoMetaRef = useRef(photoMeta);
  useEffect(() => { photoMetaRef.current = photoMeta; }, [photoMeta]);
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  // Handle Google Photos OAuth redirect-back — open options modal and clear the query param
  useEffect(() => {
    if (searchParams.get('googlePhotosConnected') === '1') {
      setGpAutoOpenOptions(n => n + 1);
      setGpConnected(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Show notification opt-in 6 s after a fresh join (only if not already enabled)
  useEffect(() => {
    if (searchParams.get('justJoined') !== '1') return;
    setSearchParams({}, { replace: true });
    const timer = setTimeout(() => {
      setNotificationsEnabled((current) => {
        // Skip if the empty-album prompt already opened the modal
        if (emptyAlbumPromptShownRef.current) return current;
        if (!current) setNotifModalOpen(true);
        return current;
      });
    }, 6000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show phone number opt-in when album loads empty — once per page visit, skip for creators.
  // Uses state.album?._id (stable string) as dep so object reference churn doesn't cancel the effect.
  useEffect(() => {
    if (emptyAlbumPromptShownRef.current) return;
    if (state.loading || state.error || !state.album?._id) return;
    if (state.photos.length > 0) return;
    if (notificationsEnabled || isCreator) return;
    emptyAlbumPromptShownRef.current = true;
    setNotifModalContext('empty-album');
    setNotifModalOpen(true);

  }, [state.loading, state.error, state.album?._id, state.photos.length, notificationsEnabled, isCreator]);

  // Fetch synced photo IDs for badge display; re-fetch when a sync job completes
  useEffect(() => {
    if (!albumId) return;
    import('../api/googlePhotos').then(({ googlePhotosApi }) =>
      googlePhotosApi.getSyncedPhotoIds(albumId)
        .then(({ syncedPhotoIds }) => setGpSyncedPhotoIds(new Set(syncedPhotoIds)))
        .catch(() => {})
    );
  }, [albumId, gpSyncJob?.status === 'completed' ? gpSyncJob._id : null]);

  // Sync failed photo IDs from the completed job into local state; reset synced
  // photos so the Synced tab re-fetches fresh data on next open.
  useEffect(() => {
    if (gpSyncJob?.status === 'completed') {
      setGpFailedPhotoIds(new Set(gpSyncJob.failedPhotoIds ?? []));
      setGpSyncedPhotosLoaded(false);
    }
  }, [gpSyncJob?.status === 'completed' ? gpSyncJob._id : null]);

  const handleRetryGpSync = useCallback(async (photoId: string) => {
    if (!albumId) return;
    setGpRetryingPhotoIds((prev) => new Set([...prev, photoId]));
    try {
      const { googlePhotosApi } = await import('../api/googlePhotos');
      await googlePhotosApi.retryPhoto(albumId, photoId);
      setGpFailedPhotoIds((prev) => { const next = new Set(prev); next.delete(photoId); return next; });
      setGpSyncedPhotoIds((prev) => new Set([...prev, photoId]));
      toast.success('Photo uploaded to Google Photos');
    } catch {
      toast.error('Upload failed — please try again');
    } finally {
      setGpRetryingPhotoIds((prev) => { const next = new Set(prev); next.delete(photoId); return next; });
    }
  }, [albumId]);

  // Close overflow menu on outside click — must check both trigger and the portaled dropdown
  useEffect(() => {
    if (!moreOpen) return;
    const handle = (e: MouseEvent) => {
      const t = e.target as Node;
      const inTrigger = moreRef.current?.contains(t);
      const inDropdown = moreDropdownRef.current?.contains(t);
      if (!inTrigger && !inDropdown) setMoreOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [moreOpen]);

  // Measure the trigger button position when the menu opens so the portaled
  // dropdown can be placed with position:fixed at the right coordinates.
  useEffect(() => {
    if (!moreOpen || !moreTriggerRef.current) return;
    const rect = moreTriggerRef.current.getBoundingClientRect();
    setMoreDropdownPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
  }, [moreOpen]);

  // Close venue display menu on outside click
  useEffect(() => {
    if (!venueMenuOpen) return;
    const handle = (e: MouseEvent) => {
      if (venueMenuRef.current && !venueMenuRef.current.contains(e.target as Node)) setVenueMenuOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [venueMenuOpen]);

  // Apply slideshow updates back into the album in state
  const handleAlbumSlideshowUpdated = useCallback((updated: Album) => {
    setState((s) => {
      if (!s.album || s.album._id !== updated._id) return s;
      return {
        ...s,
        album: {
          ...s.album,
          slideshowEnabled: updated.slideshowEnabled ?? s.album.slideshowEnabled,
          slideshowToken: updated.slideshowToken !== undefined ? updated.slideshowToken : s.album.slideshowToken,
          slideshowConfig: updated.slideshowConfig ?? s.album.slideshowConfig,
        },
      };
    });
  }, []);

  // DevTools detection
  const devToolsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useDevToolsDetection(useCallback((method: 'resize' | 'keyboard') => {
    if (devToolsTimerRef.current) return;
    devToolsTimerRef.current = setTimeout(() => {
      devToolsTimerRef.current = null;
    }, 5000);
    if (albumId) {
      albumsApi.logDevTools(albumId, method).catch(() => {/* silent */});
    }
  }, [albumId]));

  // Face recognition: whether the album has face rec enabled
  const faceEnabled = state.album?.faceRecognitionEnabled ?? false;
  const portal = usePortalStore((s) => s.portal);
  // `matched_only` is a GUEST restriction. The creator is exempt ONLY in Creator
  // Mode — in Viewer Mode they experience the album exactly as a guest would.
  // When restricted, there is no "All Photos" tab (they can't see the whole
  // album); they get Highlights + My Photos instead.
  const restrictedView =
    faceEnabled &&
    state.album?.guestVisibility === 'matched_only' &&
    (portal === 'viewer' || !isCreator);
  const hasFacePhoto = user?.hasFacePhoto ?? false;

  // A restricted viewer has no All Photos tab. If activeTab is still the initial
  // 'all', treat it as Highlights (their default landing view). Deriving this
  // synchronously — vs. flipping activeTab in an effect — guarantees the full
  // album never flashes to a restricted viewer.
  const effectiveTab: 'all' | 'my-photos' | 'gp-synced' | 'highlights' =
    restrictedView && activeTab === 'all' ? 'highlights' : activeTab;
  const showMyPhotos = faceEnabled && effectiveTab === 'my-photos';
  const showHighlights = effectiveTab === 'highlights';
  const showGpSynced = effectiveTab === 'gp-synced';
  const showAllPhotos = effectiveTab === 'all';

  // Fetch my photos — called when switching to My Photos tab or after selfie upload
  const fetchMyPhotos = useCallback(async (force = false) => {
    if (!albumId || !faceEnabled) return;
    if (!force && myPhotosLoaded) return;
    setMyPhotosLoading(true);
    setMyPhotosError(null);
    try {
      const result = await albumsApi.getMyPhotos(albumId);
      setMyPhotoIds(new Set(result.matchedPhotoIds));
      setReviewPhotos(result.reviewPhotos ?? []);
      setViewerHighlightIds(new Set([...result.highlightPhotoIds, ...result.pinnedPhotoIds]));
      setMyPhotosLoaded(true);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { code?: string; error?: string } } };
      if (e?.response?.data?.code === 'NO_FACE_PHOTO') {
        // User hasn't uploaded a selfie yet — show the prompt
        setMyPhotosError(null);
      } else {
        const msg = e?.response?.data?.error ?? 'Failed to load your photos';
        setMyPhotosError(msg);
      }
    } finally {
      setMyPhotosLoading(false);
    }
  }, [albumId, faceEnabled, myPhotosLoaded]);

  // Review strip: a confirmed ✓ candidate joins My Photos immediately (merged
  // into the grid without a refetch); a rejected ✗ just drops out of the strip.
  const handleReviewConfirmed = useCallback((photoId: string) => {
    const photo = reviewPhotos.find((p) => p._id === photoId);
    setReviewPhotos((prev) => prev.filter((p) => p._id !== photoId));
    if (!photo) return;
    setState((s) => {
      if (s.photos.some((p) => p._id === photoId)) return s;
      const merged: Photo = {
        _id: photo._id,
        eventId: albumId ?? '',
        ftpAccountId: null,
        originalFilename: 'photo',
        storagePath: '',
        thumbnailPath: '',
        fileSizeBytes: 0,
        uploadedAt: new Date().toISOString(),
        thumbnailUrl: photo.thumbnailUrl,
        originalUrl: photo.originalUrl,
      };
      return { ...s, photos: [merged, ...s.photos] };
    });
    setMyPhotoIds((ids) => new Set(ids).add(photoId));
  }, [reviewPhotos, albumId]);

  const handleReviewRejected = useCallback((photoId: string) => {
    setReviewPhotos((prev) => prev.filter((p) => p._id !== photoId));
  }, []);

  // Fetch full photo objects for synced photos — merges them into state.photos so
  // the viewer works correctly and displayPhotos can filter without needing all pages.
  const fetchSyncedPhotos = useCallback(async (force = false) => {
    if (!albumId) return;
    if (!force && gpSyncedPhotosLoaded) return;
    setGpSyncedPhotosLoading(true);
    try {
      const { photos: syncedPhotos, syncedPhotoIds } = await googlePhotosApi.getSyncedPhotos(albumId);
      setGpSyncedPhotoIds(new Set(syncedPhotoIds));
      setState((s) => {
        const existingIds = new Set(s.photos.map((p) => p._id));
        const newPhotos = syncedPhotos.filter((p) => !existingIds.has(p._id));
        if (newPhotos.length === 0) return s;
        return { ...s, photos: [...s.photos, ...newPhotos] };
      });
      setGpSyncedPhotosLoaded(true);
    } catch {
      // silent — tab will show empty state
    } finally {
      setGpSyncedPhotosLoading(false);
    }
  }, [albumId, gpSyncedPhotosLoaded]);

  // Handle tab switch
  const handleTabChange = useCallback((tab: 'all' | 'my-photos' | 'gp-synced' | 'highlights') => {
    const tabOrder: (typeof tab)[] = ['all', 'highlights', 'my-photos', 'gp-synced'];
    tabDirectionRef.current = tabOrder.indexOf(tab) > tabOrder.indexOf(activeTab) ? 1 : -1;
    setActiveTab(tab);
    if (tab === 'my-photos') {
      if (!hasFacePhoto && !selfieJustUploaded) {
        setSelfiePromptOpen(true);
      } else if (hasFacePhoto) {
        // Always ensure we have fresh My Photos data when switching to this tab
        if (!myPhotosLoaded || myPhotoIds.size === 0) {
          fetchMyPhotos();
        }
      }
    } else if (tab === 'highlights') {
      // Highlight ids ride along on the getMyPhotos response — make sure it has run.
      if (!myPhotosLoaded) fetchMyPhotos();
    } else if (tab === 'gp-synced') {
      if (gpConnected === null) {
        setGpConnectChecking(true);
        googlePhotosApi.getStatus()
          .then(({ connected }) => {
            setGpConnected(connected);
            if (connected) fetchSyncedPhotos();
          })
          .catch(() => setGpConnected(false))
          .finally(() => setGpConnectChecking(false));
      } else if (gpConnected) {
        fetchSyncedPhotos();
      }
    }
  }, [activeTab, hasFacePhoto, selfieJustUploaded, myPhotosLoaded, myPhotoIds, fetchMyPhotos, gpConnected, fetchSyncedPhotos]);

  // Auto-fetch on mount for any face-enabled album the viewer is a member of,
  // so the My Photos tab + count are ready, and (for restricted viewers) so the
  // All Photos tab knows which highlights/matches to show. Without a selfie there
  // are no matches yet; they populate once the viewer uploads one.
  useEffect(() => {
    if (faceEnabled && !myPhotosLoaded && albumId) {
      fetchMyPhotos();
    }
  }, [faceEnabled, myPhotosLoaded, albumId, fetchMyPhotos]);

  // Clear MyPhotos state when the user's face photo is removed (e.g. deleted from Settings)
  useEffect(() => {
    if (!hasFacePhoto) {
      setMyPhotoIds(new Set());
      setMyPhotosLoaded(false);
    }
  }, [hasFacePhoto]);

  // After selfie captured — show loading immediately, then search
  const handleSelfieCaptured = useCallback(() => {
    setSelfiePromptOpen(false);
    setSelfieJustUploaded(true);
    setMyPhotosLoaded(false);
    setMyPhotosLoading(true);
    setActiveTab('my-photos');
    // fetchMe already ran in SelfiePrompt — fetch my photos directly
    fetchMyPhotos(true);
  }, [fetchMyPhotos]);

  // The My Photos tab always shows the viewer's own face matches ONLY.
  const myPhotosVisibleCount = myPhotoIds.size;

  // "Everything a restricted viewer is allowed to see" = their matches +
  // highlights/pins. Still used by the Google Photos sync button (sync-all set).
  const restrictedAllPhotoIds = useMemo(
    () => new Set<string>([...myPhotoIds, ...viewerHighlightIds]),
    [myPhotoIds, viewerHighlightIds],
  );

  // Highlights = event highlights + pinned photos (data comes from getMyPhotos,
  // so it's only available on face-recognition albums). The tab is always shown
  // to restricted viewers — it's their main gallery, replacing All Photos — and
  // to open-album viewers only once there's at least one highlight, so plain
  // galleries don't get an empty tab.
  const highlightsCount = viewerHighlightIds.size;
  const showHighlightsTab = faceEnabled && (restrictedView || highlightsCount > 0);

  const allPhotosTab = { id: 'all' as const, label: `All Photos${state.totalPhotoCount > 0 ? ` (${state.totalPhotoCount})` : ''}`, icon: Images };
  const highlightsTab = { id: 'highlights' as const, label: `Highlights${highlightsCount > 0 ? ` (${highlightsCount})` : ''}`, icon: Sparkles };
  const myPhotosTab = { id: 'my-photos' as const, label: `My Photos${myPhotosLoaded ? ` (${myPhotosVisibleCount})` : ''}`, icon: ScanFace };
  const syncedTab = { id: 'gp-synced' as const, label: `Synced${gpSyncedPhotoIds.size > 0 ? ` (${gpSyncedPhotoIds.size})` : ''}`, icon: GooglePhotosIcon };

  // Restricted: no All Photos (they can't see the whole album) — Highlights leads
  // and is the default. Open: All Photos leads, Highlights slots in after My Photos.
  const albumTabs: TabItem<'all' | 'my-photos' | 'gp-synced' | 'highlights'>[] = restrictedView
    ? [
        ...(showHighlightsTab ? [highlightsTab] : []),
        ...(faceEnabled ? [myPhotosTab] : []),
        syncedTab,
      ]
    : [
        allPhotosTab,
        ...(faceEnabled ? [myPhotosTab] : []),
        ...(showHighlightsTab ? [highlightsTab] : []),
        syncedTab,
      ];

  useEffect(() => {
    if (!albumId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    albumsApi
      .viewAlbum(albumId)
      .then((data) => {
        cursorRef.current = data.nextCursor;
        setState({
          album: data.album,
          photos: data.photos,
          hasMore: data.hasMore,
          totalPhotoCount: data.totalPhotoCount,
          loading: false,
          loadingMore: false,
          error: null,
          coverPhotoUrl: data.coverPhotoUrl ?? null,
        });
        if (data.photoMeta) setPhotoMeta(data.photoMeta);
        if (data.isCreator != null) setIsCreator(data.isCreator);
        if (data.stats) setActivityStats(data.stats);
        setNotificationsEnabled(data.memberNotificationsEnabled ?? false);
        track(EVENTS.ALBUM_VIEW_OPENED, { album_id: data.album._id, album_name: data.album.name });
      })
      .catch((err) => {
        const status = err?.response?.status;
        const data = err?.response?.data;
        const isDomainRestricted = status === 403 && data?.code === 'DOMAIN_RESTRICTED';
        setState((s) => ({
          ...s,
          loading: false,
          error: status === 403 ? 403 : status === 404 ? 404 : 'unknown',
          // A domain-restricted member has an invite token too, but we must NOT
          // bounce them to /join (that would just re-block them) — route through
          // the restricted screen instead.
          notMemberInviteToken: (status === 403 && !isDomainRestricted) ? (data?.inviteToken ?? null) : null,
          restrictedEmailDomains: isDomainRestricted
            ? { domains: data?.allowedEmailDomains ?? [], inviteToken: data?.inviteToken ?? null }
            : null,
        }));
      });
  }, [albumId]);

  // Refetch when tab becomes visible again (handles signed URL expiry)
  useEffect(() => {
    if (!albumId) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        albumsApi.viewAlbum(albumId).then((data) => {
          cursorRef.current = data.nextCursor;
          setState((s) => ({
            ...s,
            album: data.album,
            photos: data.photos,
            hasMore: data.hasMore,
            totalPhotoCount: data.totalPhotoCount,
          }));
          if (data.stats) setActivityStats(data.stats);
        }).catch((err) => {
          // If access was revoked while the tab was backgrounded (e.g. the
          // creator added a domain restriction), surface the restricted screen
          // on refocus instead of silently keeping stale content.
          const status = err?.response?.status;
          const data = err?.response?.data;
          if (status === 403 && data?.code === 'DOMAIN_RESTRICTED') {
            setState((s) => ({
              ...s,
              error: 403,
              notMemberInviteToken: null,
              restrictedEmailDomains: { domains: data?.allowedEmailDomains ?? [], inviteToken: data?.inviteToken ?? null },
            }));
          }
          /* other errors: silent — keep showing current content */
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [albumId]);

  const loadMore = useCallback(() => {
    const cursor = cursorRef.current;
    if (!albumId || !cursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setState((s) => ({ ...s, loadingMore: true }));
    albumsApi
      .viewAlbum(albumId, cursor)
      .then((data) => {
        cursorRef.current = data.nextCursor;
        loadingMoreRef.current = false;
        setState((s) => ({
          ...s,
          photos: [...s.photos, ...data.photos],
          hasMore: data.hasMore,
          loadingMore: false,
        }));
        if (data.photoMeta) setPhotoMeta((prev) => ({ ...prev, ...data.photoMeta }));
      })
      .catch(() => {
        loadingMoreRef.current = false;
        setState((s) => ({ ...s, loadingMore: false }));
        toast.error('Failed to load more photos');
      });
  }, [albumId]);

  const loadMoreFnRef = useRef(loadMore);
  useEffect(() => { loadMoreFnRef.current = loadMore; }, [loadMore]);

  // Auto-load remaining photos the current tab needs — they may live on pages
  // not yet paginated in. The My Photos tab needs every face match; the
  // Highlights tab needs every highlight/pin it filters down to.
  useEffect(() => {
    const { hasMore, photos } = state;
    if (!faceEnabled || !myPhotosLoaded || myPhotosLoading || !hasMore || loadingMoreRef.current) return;

    // A restricted viewer's initial 'all' resolves to the Highlights tab.
    const onHighlights = activeTab === 'highlights' || (restrictedView && activeTab === 'all');
    let requiredIds: Set<string> | null = null;
    if (activeTab === 'my-photos') requiredIds = myPhotoIds;
    else if (onHighlights) requiredIds = viewerHighlightIds;
    if (!requiredIds) return;

    const loadedIds = new Set(photos.map((p) => p._id));
    if ([...requiredIds].some((id) => !loadedIds.has(id))) {
      loadMore();
    }
  }, [faceEnabled, activeTab, restrictedView, myPhotosLoaded, myPhotosLoading, state, myPhotoIds, viewerHighlightIds, loadMore]);


  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelCallbackRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreFnRef.current(); },
      { rootMargin: '300px' },
    );
    observerRef.current.observe(el);
  }, []);

  const handleLeave = async () => {
    if (!albumId) return;
    setLeaving(true);
    try {
      await albumsApi.leaveAlbum(albumId);
      track(EVENTS.ALBUM_LEFT, { album_id: albumId });
      navigate('/albums');
    } catch {
      setLeaving(false);
      setShowLeaveModal(false);
    }
  };

  const handleToggleNotifications = async () => {
    if (!albumId) return;
    setMoreOpen(false);
    // Turning on but no phone — open the opt-in modal to collect/verify phone first
    if (!notificationsEnabled && !user?.phone) {
      setNotifModalOpen(true);
      return;
    }
    // Toggle directly when phone is already on file (or turning off)
    const next = !notificationsEnabled;
    setNotificationsEnabled(next);
    try {
      await albumsApi.updateNotifications(albumId, next);
      toast.success(next ? 'Notifications enabled' : 'Notifications turned off');
    } catch {
      setNotificationsEnabled(!next);
      toast.error('Failed to update notification preference');
    }
  };

  // ── Like helpers ────────────────────────────────────────────────────────────

  // executeLike reads photoMeta via ref so it never needs to be re-created
  // when like state changes — keeping it (and all downstream callbacks) stable.
  const executeLike = useCallback(async (photoId: string) => {
    if (!albumId) return;
    const wasLiked = photoMetaRef.current[photoId]?.isLikedByMe ?? false;
    const likeDelta = wasLiked ? -1 : 1;
    setPhotoMeta((prev) => {
      const cur = prev[photoId] ?? { likeCount: 0, isLikedByMe: false, isPinned: false };
      return {
        ...prev,
        [photoId]: {
          ...cur,
          likeCount: cur.isLikedByMe ? Math.max(0, cur.likeCount - 1) : cur.likeCount + 1,
          isLikedByMe: !cur.isLikedByMe,
        },
      };
    });
    setActivityStats((prev) => prev ? { ...prev, totalLikes: Math.max(0, prev.totalLikes + likeDelta) } : prev);
    try {
      const result = await albumsApi.toggleLike(albumId, photoId);
      setPhotoMeta((prev) => ({
        ...prev,
        [photoId]: { ...prev[photoId], likeCount: result.likeCount, isLikedByMe: result.liked },
      }));
    } catch {
      setPhotoMeta((prev) => {
        const cur = prev[photoId] ?? { likeCount: 0, isLikedByMe: false, isPinned: false };
        return {
          ...prev,
          [photoId]: {
            ...cur,
            likeCount: cur.isLikedByMe ? Math.max(0, cur.likeCount - 1) : cur.likeCount + 1,
            isLikedByMe: !cur.isLikedByMe,
          },
        };
      });
      setActivityStats((prev) => prev ? { ...prev, totalLikes: Math.max(0, prev.totalLikes - likeDelta) } : prev);
    }
  }, [albumId]); // stable — no photoMeta dependency

  // Signature: (photoId, e) — cell calls onToggleLike(photo._id, e)
  const handleToggleLike = useCallback((photoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!albumId) return;
    if (userRef.current && userRef.current.likeVisibility === null) {
      pendingLikeRef.current = { e, photoId };
      setPrivacyPromptOpen(true);
      return;
    }
    executeLike(photoId);
  }, [albumId, executeLike]); // stable — reads user via ref

  const handlePrivacyChoice = useCallback(async (visibility: LikeVisibility) => {
    try {
      const updated = await authApi.updatePreferences({ likeVisibility: visibility });
      setUser(updated);
      setPrivacyPromptOpen(false);

      // Execute the pending like that triggered the prompt
      if (pendingLikeRef.current) {
        const { photoId } = pendingLikeRef.current;
        pendingLikeRef.current = null;
        executeLike(photoId);
      }
    } catch {
      toast.error('Failed to save preference. Please try again.');
    }
  }, [setUser, executeLike]);

  // ── Pin helpers (creator only) ────────────────────────────────────────────

  // Signature: (photoId, e) — cell calls onTogglePin(photo._id, e)
  const handleTogglePin = useCallback(async (photoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!albumId) return;
    setPhotoMeta((prev) => {
      const cur = prev[photoId] ?? { likeCount: 0, isLikedByMe: false, isPinned: false };
      return { ...prev, [photoId]: { ...cur, isPinned: !cur.isPinned } };
    });
    try {
      const result = await albumsApi.togglePin(albumId, photoId);
      setPhotoMeta((prev) => ({
        ...prev,
        [photoId]: { ...prev[photoId], isPinned: result.isPinned },
      }));
    } catch (err: unknown) {
      setPhotoMeta((prev) => {
        const cur = prev[photoId] ?? { likeCount: 0, isLikedByMe: false, isPinned: false };
        return { ...prev, [photoId]: { ...cur, isPinned: !cur.isPinned } };
      });
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Failed to toggle pin');
    }
  }, [albumId]);

  // ── Select mode helpers ────────────────────────────────────────────────────

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      if (next.size === 0) setSelectMode(false);
      return next;
    });
  }, []);

  const handleView = useCallback((index: number) => setViewerIndex(index), []);

  const handleBadgeClick = useCallback((photoId: string) => {
    setSelectMode(true);
    setSelectedIds(new Set([photoId]));
  }, []);

  // ── "Not me" (My Photos tab) ───────────────────────────────────────────────
  // The viewer says an auto-shown face match isn't them. Optimistically drop it
  // from the My Photos grid + count, then tell the backend to LEARN: `reject`
  // permanently excludes this photo for this member and forgets any face template
  // learned from it, so it never resurfaces on future searches. A blip self-heals
  // on the next load, so we don't block the UI on the request. An Undo restores
  // it (a mis-tap would otherwise hide a real photo with no way back).
  const handleNotMe = useCallback((photoId: string) => {
    if (!albumId) return;
    const wasMatched = myPhotoIds.has(photoId);

    setMyPhotoIds((ids) => {
      if (!ids.has(photoId)) return ids;
      const next = new Set(ids);
      next.delete(photoId);
      return next;
    });
    setReviewPhotos((prev) => prev.filter((p) => p._id !== photoId));

    albumsApi.confirmMyPhoto(albumId, photoId, 'reject').catch(() => {
      if (wasMatched) setMyPhotoIds((ids) => new Set(ids).add(photoId));
      toast.error("Couldn't update — please try again");
    });

    const undo = () => {
      setMyPhotoIds((ids) => new Set(ids).add(photoId));
      albumsApi.confirmMyPhoto(albumId, photoId, 'confirm').catch(() => {
        setMyPhotoIds((ids) => {
          const next = new Set(ids);
          next.delete(photoId);
          return next;
        });
        toast.error("Couldn't undo — please try again");
      });
    };

    toast((t) => (
      <span className={styles['notme-toast']}>
        Removed from your photos
        <button
          type="button"
          className={styles['notme-toast-undo']}
          onClick={() => { undo(); toast.dismiss(t.id); }}
        >
          Undo
        </button>
      </span>
    ));
  }, [albumId, myPhotoIds]);

  const handleReport = useCallback((photoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setReportPhotoId(photoId);
  }, []);

  const handleShare = useCallback(async (photoId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();

    // If native share is not supported, fallback to custom sheet
    if (typeof navigator === 'undefined' || !navigator.share) {
      setSharePhotoId(photoId);
      return;
    }

    const { photos, album } = stateRef.current;
    const sharePhoto = photos.find((p) => p._id === photoId);
    if (!sharePhoto) return;

    const photoUrl = sharePhoto.processedUrl ?? sharePhoto.originalUrl ?? sharePhoto.thumbnailUrl ?? '';
    if (!photoUrl) return;

    const albumName = album?.name ?? 'Album';
    const evName = (album?.eventId && typeof album.eventId === 'object') ? (album.eventId as EventSummary).name : '';
    const shareText = evName
      ? `Check out this photo from ${albumName} (${evName})`
      : `Check out this photo from ${albumName}`;

    try {
      let cdnUrl = photoUrl;
      try {
        const parsed = new URL(photoUrl);
        if (parsed.hostname.includes('pixel-machha-photos.s3')) {
          cdnUrl = `https://d3ic3ju44zc444.cloudfront.net${parsed.pathname}`;
        }
      } catch {
        // not a parseable URL — fall back to the original photoUrl
      }

      const res = await fetch(cdnUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const filename = `${albumName.replace(/\s+/g, '-').toLowerCase()}-photo.jpg`;
      const file = new File([blob], filename, { type: 'image/jpeg' });

      const shareData: ShareData = {
        title: albumName,
        text: shareText,
        files: [file],
      };

      if (navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.share({ title: albumName, text: shareText, url: photoUrl });
      }
    } catch (err) {
      // If user cancelled or fetching failed, fallback to custom sheet
      if ((err as Error)?.name !== 'AbortError') {
        setSharePhotoId(photoId);
      }
    }
  }, []);

  const handleLikeCountTap = useCallback((photoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLikersPhotoId(photoId);
  }, []);

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setGpSyncSelectFilter(null);
  };

  const handleGpSyncSelected = async () => {
    if (!albumId || selectedIds.size === 0) return;
    const alreadySynced = Array.from(selectedIds).filter((id) => gpSyncedPhotoIds.has(id)).length;
    if (alreadySynced > 0) {
      toast(`${alreadySynced} of ${selectedIds.size} selected photo${alreadySynced !== 1 ? 's have' : ' has'} already been synced — uploading again will create duplicates`, { duration: 4000 });
    }
    setGpSyncingSelected(true);
    const ids = Array.from(selectedIds);
    try {
      await import('../api/googlePhotos').then(({ googlePhotosApi }) =>
        googlePhotosApi.startSync(albumId, {
          scope: gpSyncSelectFilter === 'my_photos' ? 'my_photos' : 'selected',
          photoIds: ids,
        })
      );
      // Optimistic update — show badges immediately without waiting for job completion
      setGpSyncedPhotoIds((prev) => new Set([...prev, ...ids]));
      setGpConnected(true);
      setGpSyncJobTrigger((n) => n + 1);
      exitSelectMode();
      toast.success('Sync started — check the Google Photos button for progress');
    } catch {
      toast.error('Failed to start sync. Please try again.');
    } finally {
      setGpSyncingSelected(false);
    }
  };

  const handleGpSyncStarted = useCallback((scope: SyncScope, photoIds?: string[]) => {
    setGpConnected(true);
    setGpSyncedPhotoIds((prev) => {
      if (scope === 'selected' && photoIds) {
        return new Set([...prev, ...photoIds]);
      }
      if (scope === 'my_photos') {
        return new Set([...prev, ...myPhotoIds]);
      }
      // 'all' — add all currently loaded photo IDs
      return new Set([...prev, ...state.photos.map((p) => p._id)]);
    });
  }, [myPhotoIds, state.photos]);


  /** Download photos as a zip.
   *
   * Priority chain per photo:
   *   1. processedUrl (server-composited, overlays baked in)
   *   2. client-side composite of originalUrl + album.overlayConfigs — only
   *      when the album has overlays AND server-side processing didn't deliver
   *      a processedUrl. Reduces "fallback" from "originals without overlays"
   *      (the previous behaviour) to "originals WITH overlays applied locally".
   *   3. originalUrl (no overlays — final fallback when even the compositor fails)
   */
  const downloadPhotosAsZip = async (photosToDownload: Photo[], label: string) => {
    const toastId = 'album-download';
    toast.loading(`Preparing ${photosToDownload.length} photo${photosToDownload.length !== 1 ? 's' : ''} for download…`, { id: toastId });
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const folderName = label.replace(/\s+/g, '-').toLowerCase();
      const folder = zip.folder(folderName)!;

      const albumOverlayConfigs = state.album?.overlayConfigs ?? [];
      const hasOverlays = albumOverlayConfigs.length > 0;

      let succeeded = 0;
      let failed = 0;
      let clientComposited = 0;

      for (let i = 0; i < photosToDownload.length; i += 4) {
        const batch = photosToDownload.slice(i, i + 4);
        const results = await Promise.allSettled(
          batch.map(async (photo) => {
            let blob: Blob | undefined;
            let name = photo.originalFilename;

            // Priority 1: Pre-processed image from S3 (overlays already baked in by Lambda)
            if (photo.processedUrl) {
              try {
                const r = await fetch(photo.processedUrl);
                if (r.ok) {
                  blob = await r.blob();
                  name = photo.originalFilename.replace(/\.[^.]+$/, '') + '.jpg';
                }
              } catch {
                // Fall through to client compositor / original
              }
            }

            // Priority 2: Client-side composite — only when the album has
            // overlays and server compositing didn't produce a processedUrl
            // (e.g. processingStatus === 'failed'). Without this, the user
            // would receive the bare original which the failed-banner explicitly
            // promised to avoid.
            if (!blob && hasOverlays && photo.originalUrl) {
              try {
                blob = await compositePhotoToBlob({
                  baseUrl: photo.originalUrl,
                  overlayConfigs: albumOverlayConfigs,
                });
                name = photo.originalFilename.replace(/\.[^.]+$/, '') + '.jpg';
                clientComposited++;
              } catch (err) {
                console.warn('[downloadPhotosAsZip] Client-side composite failed for photo, will fall through to original:', err);
              }
            }

            // Priority 3: Original image (no overlays — last resort)
            if (!blob) {
              if (!photo.originalUrl) throw new Error('No URL');
              const r = await fetch(photo.originalUrl);
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              blob = await r.blob();
              name = photo.originalFilename;
            }

            folder.file(name, blob);
          }),
        );
        results.forEach((r) => {
          if (r.status === 'fulfilled') succeeded++;
          else { failed++; console.warn('Photo download failed:', r.reason); }
        });
        const done = Math.min(i + 4, photosToDownload.length);
        const stageLabel = clientComposited > 0
          ? `Compositing & downloading ${done}/${photosToDownload.length} photos…`
          : `Downloading ${done}/${photosToDownload.length} photos…`;
        toast.loading(stageLabel, { id: toastId });
      }

      if (succeeded === 0) {
        toast.error('All photo downloads failed. Try refreshing the page.', { id: toastId });
        return;
      }

      toast.loading(`Compressing zip…`, { id: toastId });
      const content = await zip.generateAsync({ type: 'blob', compression: 'STORE', streamFiles: true });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${folderName}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(
        failed > 0
          ? `${succeeded} photo${succeeded !== 1 ? 's' : ''} downloaded (${failed} failed)`
          : `${succeeded} photo${succeeded !== 1 ? 's' : ''} downloaded!`,
        { id: toastId },
      );
    } catch (err) {
      console.error('Album download failed:', err);
      toast.error('Download failed', { id: toastId });
    }
  };

  /**
   * Restricted (matched_only face-rec) viewers download the shared, pre-built
   * highlights+pins ZIP from S3. If it isn't built yet, fall back to a
   * client-side zip of the loaded highlights/pins — the server has been asked to
   * build it, so the next attempt is fast. `downloadPhotosAsZip` owns the toast.
   */
  const downloadRestrictedHighlightsZip = async (toastId: string) => {
    if (!albumId) return;
    const result = await albumsApi.downloadHighlightsZip(albumId);
    if (result === 'building') {
      const fallback = photos.filter((p) => viewerHighlightIds.has(p._id));
      if (fallback.length > 0) {
        await downloadPhotosAsZip(fallback, album?.name ?? 'highlights');
      } else {
        toast.error('Download not yet available — it will be ready soon. Please try again in a moment.', { id: toastId, duration: 5000 });
      }
    } else {
      toast.success('Download started!', { id: toastId });
    }
  };

  const handleBatchDownload = async () => {
    if (!albumId) return;
    setBatchDownloading(true);

    // Client-side archiver: the selection goes straight through as ids. No
    // 500-id cap, so the old isWholeAlbum / isHighlightsSelectAll special-casing
    // (which existed purely to dodge that cap) isn't needed here.
    if (CLIENT_ZIP_ENABLED) {
      const ids = Array.from(selectedIds);
      track(EVENTS.ALBUM_PHOTO_DOWNLOADED, {
        album_id: albumId,
        album_name: album?.name,
        download_type: ids.length === 1 ? 'single' : 'batch',
        photo_count: ids.length,
        client_zip: true,
      });
      try {
        await startClientZip({
          target: { kind: 'album', id: albumId },
          albumName: album?.name ?? 'album',
          scope: 'selection',
          photoIds: ids,
        });
      } catch (err) {
        console.error('[handleBatchDownload] client zip failed:', err);
      }
      setBatchDownloading(false);
      return;
    }

    // The server zips by id, so include every selected id — even ones whose
    // photo objects aren't loaded (ids-only "Select all"). The loaded subset is
    // only needed for the client-side fallback below.
    const selectedPhotoIds = Array.from(selectedIds);
    const selectedLoaded = photos.filter((p) => selectedIds.has(p._id));
    const count = selectedPhotoIds.length;
    // Selecting the whole All-Photos album? Use the uncapped pre-built ZIP
    // instead of the selective endpoint (capped at 500 ids).
    const isWholeAlbum = showAllPhotos && count >= (totalPhotoCount || count);
    // Selecting the whole Highlights set → serve the shared, pre-built
    // highlights+pins ZIP (not the per-id endpoint, whose fallback only covers
    // the photos scrolled into view).
    const isHighlightsSelectAll =
      showHighlights && viewerHighlightIds.size > 0 && count >= viewerHighlightIds.size;
    const downloadType = count === 1 ? 'single' : 'batch' as const;
    track(EVENTS.ALBUM_PHOTO_DOWNLOADED, {
      album_id: albumId,
      album_name: album?.name,
      download_type: downloadType,
      photo_count: count,
    });
    const toastId = 'album-download';

    if (isHighlightsSelectAll) {
      toast.loading('Preparing your download…', { id: toastId });
      try {
        await downloadRestrictedHighlightsZip(toastId);
      } catch (err) {
        console.error('Highlights batch download failed, falling back to client-side:', err);
        try {
          await downloadPhotosAsZip(selectedLoaded, album?.name ?? 'highlights');
        } catch {
          toast.error('Download failed', { id: toastId });
        }
      }
      setBatchDownloading(false);
      return;
    }

    toast.loading(`Preparing ${count} photo${count !== 1 ? 's' : ''} for download…`, { id: toastId });
    try {
      const result = await albumsApi.downloadZip(albumId, isWholeAlbum ? undefined : selectedPhotoIds);
      if (result === 'building') {
        toast.error('Download not yet available — it will be ready soon. Please try again in a moment.', { id: toastId, duration: 5000 });
      } else {
        toast.success(`${count} photo${count !== 1 ? 's' : ''} downloaded!`, { id: toastId });
      }
    } catch (err) {
      console.error('Batch download failed, falling back to client-side:', err);
      toast.loading('Server download failed, trying client-side…', { id: toastId });
      try {
        await downloadPhotosAsZip(selectedLoaded, album?.name ?? 'album');
      } catch {
        toast.error('Download failed', { id: toastId });
      }
    }
    setBatchDownloading(false);
  };

  const handleDownloadAll = async () => {
    if (!albumId) return;
    setDownloadingAll(true);

    const toastId = 'album-download';

    // Client-side archiver: one path for every tab. The DownloadPanel owns
    // progress, so there's no toast and no "come back later while we build it".
    if (CLIENT_ZIP_ENABLED) {
      const scope = showMyPhotos ? 'my-photos' : showHighlights ? 'highlights' : 'all';
      const count = showMyPhotos
        ? myPhotoIds.size
        : showHighlights
          ? viewerHighlightIds.size
          : totalPhotoCount;
      track(EVENTS.ALBUM_PHOTO_DOWNLOADED, {
        album_id: albumId,
        album_name: album?.name,
        download_type: scope,
        photo_count: count,
        client_zip: true,
      });
      try {
        // My Photos is the viewer's own face matches — the server can't derive
        // that set from a scope alone, so send the ids the tab already resolved.
        await startClientZip({
          target: { kind: 'album', id: albumId },
          albumName: album?.name ?? 'album',
          scope,
          photoIds: showMyPhotos ? Array.from(myPhotoIds) : undefined,
        });
      } catch (err) {
        console.error('[handleDownloadAll] client zip failed:', err);
      }
      setDownloadingAll(false);
      return;
    }

    // My Photos: per-viewer face matches — client-side only (can't be pre-built).
    if (showMyPhotos) {
      const photosToDownload = photos.filter((p) => myPhotoIds.has(p._id));
      track(EVENTS.ALBUM_PHOTO_DOWNLOADED, {
        album_id: albumId,
        album_name: album?.name,
        download_type: 'my-photos',
        photo_count: photosToDownload.length,
      });
      toast.loading(`Preparing ${photosToDownload.length} photo${photosToDownload.length !== 1 ? 's' : ''} for download…`, { id: toastId });
      try {
        await downloadPhotosAsZip(photosToDownload, album?.name ?? 'my-photos');
      } catch {
        toast.error('Download failed', { id: toastId });
      }
      setDownloadingAll(false);
      return;
    }

    // Highlights: the shared, pre-built highlights+pins ZIP.
    if (showHighlights) {
      track(EVENTS.ALBUM_PHOTO_DOWNLOADED, {
        album_id: albumId,
        album_name: album?.name,
        download_type: 'highlights',
        photo_count: viewerHighlightIds.size,
      });
      toast.loading('Preparing your download…', { id: toastId });
      try {
        await downloadRestrictedHighlightsZip(toastId);
      } catch (err) {
        console.error('[handleDownloadAll] Highlights ZIP failed:', err);
        toast.error('Download failed — please try again.', { id: toastId });
      }
      setDownloadingAll(false);
      return;
    }

    // All Photos: server pre-built whole-album ZIP (Lambda keeps this fresh in S3).
    track(EVENTS.ALBUM_PHOTO_DOWNLOADED, {
      album_id: albumId,
      album_name: album?.name,
      download_type: 'all',
      photo_count: totalPhotoCount,
    });
    toast.loading(`Preparing ${totalPhotoCount} photo${totalPhotoCount !== 1 ? 's' : ''} for download…`, { id: toastId });
    try {
      const result = await albumsApi.downloadZip(albumId);
      if (result === 'building') {
        toast.error('Download not yet available — it will be ready soon. Please try again in a moment.', { id: toastId, duration: 5000 });
      } else {
        toast.success(`${totalPhotoCount} photo${totalPhotoCount !== 1 ? 's' : ''} downloaded!`, { id: toastId });
      }
    } catch (err) {
      console.error('[handleDownloadAll] Server ZIP failed:', err);
      toast.error('Download failed — please try again.', { id: toastId });
    }
    setDownloadingAll(false);
  };

  const handleCopyInviteLink = () => {
    if (!album?.inviteToken) return;
    const link = `${getPortalUrl('viewer')}/join/${album.inviteToken}`;
    navigator.clipboard.writeText(link).then(() => {
      toast.success('Invite link copied!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const { album, photos, hasMore, totalPhotoCount, loading, loadingMore, error } = state;

  const displayPhotos = useMemo(() => {
    let list: typeof photos;
    if (showMyPhotos) {
      // My Photos: the viewer's own face matches only.
      list = photos.filter((p) => myPhotoIds.has(p._id));
    } else if (showHighlights) {
      // Highlights: event highlights + pinned photos only (never the viewer's
      // own private matches — those live in My Photos).
      list = photos.filter((p) => viewerHighlightIds.has(p._id));
    } else if (showGpSynced) {
      list = photos.filter((p) => gpSyncedPhotoIds.has(p._id));
    } else {
      // All Photos — the whole album. Only reachable on non-restricted albums;
      // a restricted viewer's `effectiveTab` maps 'all' → 'highlights'.
      list = [...photos];
    }

    list.sort((a, b) => {
      const aPinned = photoMeta[a._id]?.isPinned ?? false;
      const bPinned = photoMeta[b._id]?.isPinned ?? false;
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      return 0;
    });

    return list;
  }, [showMyPhotos, showHighlights, showGpSynced, photos, myPhotoIds, viewerHighlightIds, gpSyncedPhotoIds, photoMeta]);

  // O(1) index lookup instead of O(n) findIndex inside the render loop.
  // Must be keyed off displayPhotos (the tab-filtered list) so it stays in
  // sync with the array passed to AlbumPhotoViewer for next/prev navigation.
  const photoIndexMap = useMemo(
    () => new Map(displayPhotos.map((p, i) => [p._id, i])),
    [displayPhotos],
  );

  // "Select all" must cover the whole album, not just the photos scrolled into
  // view. My Photos / Synced tabs already know their complete id set up-front;
  // All Photos fetches every id in one request (no need to paginate the whole
  // album into memory just to select it).
  const handleSelectAll = async () => {
    if (showMyPhotos) { setSelectedIds(new Set(myPhotoIds)); return; }
    if (showHighlights) { setSelectedIds(new Set(viewerHighlightIds)); return; }
    if (showGpSynced) { setSelectedIds(new Set(gpSyncedPhotoIds)); return; }
    if (!albumId) return;

    setSelectingAll(true);
    try {
      setSelectedIds(new Set(await albumsApi.listPhotoIds(albumId)));
    } catch {
      toast.error('Failed to select all photos');
    } finally {
      setSelectingAll(false);
    }
  };

  // Stable reference — only recreates when creator status actually changes.
  // Pinning (album-level highlights) is available to the creator in any album,
  // not just live ones — the backend pin endpoint has no live requirement.
  const pinHandler = useMemo(
    () => isCreator ? handleTogglePin : undefined,
    [isCreator, handleTogglePin],
  );

  // ── Virtual grid ────────────────────────────────────────────────────────────
  // Measure the grid container so we can compute column count and cell size.
  // ResizeObserver fires synchronously before paint so there's no visible flash.
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(() => {
    if (typeof window === 'undefined') return 375;
    return window.innerWidth;
  });

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const gridCols = getPhotoGridCols(containerWidth);
  const gridGap  = PHOTO_GRID_GAP;
  const cellSize = Math.floor((containerWidth - (gridCols - 1) * gridGap) / gridCols);
  // Row height includes the inter-row gap so consecutive rows sit correctly.
  const rowHeight = cellSize + gridGap;

  // Group displayPhotos into rows of gridCols items.
  const photoRows = useMemo(() => {
    const rows: (typeof displayPhotos)[] = [];
    for (let i = 0; i < displayPhotos.length; i += gridCols) {
      rows.push(displayPhotos.slice(i, i + gridCols));
    }
    return rows;
  }, [displayPhotos, gridCols]);

  // scrollMargin = distance from the top of the document to the top of the
  // grid container. useWindowVirtualizer needs this to map window.scrollY to
  // a position inside the container. Measured in a layout effect so it runs
  // after the DOM is laid out but before the browser paints.
  const scrollMarginRef = useRef(0);
  useLayoutEffect(() => {
    if (gridContainerRef.current) {
      scrollMarginRef.current =
        gridContainerRef.current.getBoundingClientRect().top + window.scrollY;
    }
  });

  // +1 extra row when more photos are loading (skeleton row).
  const virtualRowCount = photoRows.length + (loadingMore ? 1 : 0);

  const virtualizer = useWindowVirtualizer({
    count: virtualRowCount,
    estimateSize: () => rowHeight,
    overscan: 3, // render 3 extra rows above/below viewport for fluid scrolling
    scrollMargin: scrollMarginRef.current,
  });

  const event: EventSummary | null =
    album?.eventId && typeof album.eventId === 'object'
      ? (album.eventId as EventSummary)
      : null;

  // Cover photo URL is resolved server-side and returned alongside the album data.
  // This guarantees the correct photo regardless of which page of results is loaded.
  const coverPhotoUrl = state.coverPhotoUrl;

  const descriptionText = album?.description || '';
  const hasDescription = descriptionText.length > 0;

  // Detect if description text is actually truncated
  useEffect(() => {
    const checkTruncation = () => {
      if (descriptionRef.current && !descExpanded) {
        const isTruncated = descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight;
        setIsDescriptionTruncated(isTruncated);
      } else {
        setIsDescriptionTruncated(false);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [descriptionText, descExpanded]);

  // SSE subscription for live albums — prepend new photos in real-time
  const isLive = album?.isLive ?? false;
  // For live albums with overlays, composite the overlay client-side while the
  // server-composited version is still pending — avoids showing a brief
  // un-overlaid flash for freshly-arrived photos. The cell/viewer only run this
  // for photos missing `processedThumbnailUrl`, so already-processed photos are
  // unaffected.
  const liveOverlayCompositor = isLive && (album?.overlayConfigs?.length ?? 0) > 0;
  useEffect(() => {
    if (!albumId || !isLive) return;
    return subscribeToAlbumStream(albumId, {
      onPhotoUpdated: (update) => {
        // Overlay compositing finished — swap the base thumbnail for the
        // server-composited (overlaid) version without a refetch.
        if (update.processedStatus !== 'processed') return;
        setState((s) => {
          const idx = s.photos.findIndex((p) => p._id === update.photoId);
          if (idx === -1) return s;
          const photos = s.photos.slice();
          photos[idx] = {
            ...photos[idx],
            processedThumbnailUrl: update.processedThumbnailUrl ?? photos[idx].processedThumbnailUrl,
            processedUrl: update.processedUrl ?? photos[idx].processedUrl,
          };
          return { ...s, photos };
        });
      },
      onPhotoAdded: (photo) => {
        // Warm the browser cache immediately — start fetching the thumbnail
        // before React re-renders the grid so the image is ready (or already
        // decoded) by the time the cell mounts. URLs are already CDN (T2).
        const preloadUrl = photo.processedThumbnailUrl ?? photo.thumbnailUrl;
        if (preloadUrl) {
          const img = new Image();
          img.src = preloadUrl;
        }
        setState((s) => {
          if (s.photos.some((p) => p._id === photo._id)) return s;
          return { ...s, photos: [photo, ...s.photos], totalPhotoCount: s.totalPhotoCount + 1 };
        });
        setPhotoMeta((prev) => ({
          ...prev,
          [photo._id]: { likeCount: 0, isLikedByMe: false, isPinned: false },
        }));
        // Bump the activity ticker photo count in sync
        setActivityStats((prev) => prev ? { ...prev, totalPhotos: prev.totalPhotos + 1 } : prev);
        // Debounced toast — batches rapid-fire photos into one notification
        newPhotosBufferRef.current += 1;
        if (sseToastTimerRef.current) clearTimeout(sseToastTimerRef.current);
        sseToastTimerRef.current = setTimeout(() => {
          const n = newPhotosBufferRef.current;
          newPhotosBufferRef.current = 0;
          toast.success(n === 1 ? 'New photo just arrived' : `${n} new photos just arrived`, {
            duration: 3000,
          });
        }, 1500);
      },
    });
  }, [albumId, isLive]);

  // ── Error states ──────────────────────────────────────────────────────────

  if (!loading && error === 403 && state.restrictedEmailDomains) {
    // Member whose email domain is no longer allowed — steer them to sign in
    // with an accepted organization email rather than showing a dead end.
    const { domains, inviteToken } = state.restrictedEmailDomains;
    const domainList = domains.map((d) => `@${d}`).join(domains.length === 2 ? ' or ' : ', ');
    const switchAccount = async () => {
      try {
        await authApi.logout();
      } catch {
        /* even if logout fails, still send them to re-authenticate */
      }
      window.location.href = authApi.googleLoginUrl(inviteToken ? `/join/${inviteToken}` : '/albums');
    };
    return (
      <div className={styles['error-state']}>
        <MachhaCharacter state="Forbidden" size={180} />
        <h2 className={styles['error-title']}>Restricted access</h2>
        <p className={styles['error-subtitle']}>
          This album is limited to members of a specific organization. Please sign in with
          your work email{domains.length > 0 ? (
            <> ending in <strong>{domainList}</strong></>
          ) : null}.
        </p>
        {user?.email && (
          <p className={styles['error-subtitle']} style={{ marginTop: '-0.25rem', opacity: 0.75 }}>
            You're currently signed in as <strong>{user.email}</strong>.
          </p>
        )}
        <div className={styles['error-action']}>
          <Button onClick={switchAccount}>Sign in with a different email</Button>
        </div>
      </div>
    );
  }

  if (!loading && error === 403) {
    // If the user is simply not a member (has invite token), redirect to the join page
    if (state.notMemberInviteToken) {
      navigate(`/join/${state.notMemberInviteToken}`, { replace: true });
      return null;
    }
    // Otherwise the user was kicked
    return (
      <div className={styles['error-state']}>
        <MachhaCharacter state="Forbidden" size={180} />
        <h2 className={styles['error-title']}>
          Access removed
        </h2>
        <p className={styles['error-subtitle']}>
          You've been removed from this album by the creator.
        </p>
        <div className={styles['error-action']}>
          <Button onClick={() => navigate('/albums')}>Back to Albums</Button>
        </div>
      </div>
    );
  }

  if (!loading && error === 404) {
    return (
      <div className={styles['error-state']}>
        <MachhaCharacter state="Not Found" size={180} />
        <h2 className={styles['error-title']}>
          Album not found
        </h2>
        <p className={styles['error-subtitle']}>
          This album no longer exists or the link is invalid.
        </p>
        <div className={styles['error-action']}>
          <Button onClick={() => navigate('/albums')}>Back to Albums</Button>
        </div>
      </div>
    );
  }

  if (!loading && error) {
    return (
      <div className={styles['error-state']}>
        <MachhaCharacter state="Error" size={180} />
        <h2 className={styles['error-title']}>
          Something went wrong
        </h2>
        <div className={styles['error-action']}>
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </div>
      </div>
    );
  }

  // ── Loading skeleton ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={styles['page']}>
        {/* Header skeleton */}
        <div className={styles['page-header-constrained']}>
          <div className={styles['skeleton-header-card']}>
            {/* actions row */}
            <div className={styles['skeleton-header-actions']}>
              <div className={styles['skeleton-icon-btn']} />
              <div className={styles['skeleton-header-actions-right']}>
                <div className={styles['skeleton-icon-btn']} style={{ width: '8rem', borderRadius: '999px' }} />
                <div className={styles['skeleton-icon-btn']} />
              </div>
            </div>
            {/* title + meta */}
            <div className={styles['skeleton-header-info']}>
              <div className={styles['skeleton-header-title']} />
              <div className={styles['skeleton-header-meta']} />
              <div className={styles['skeleton-header-meta']} style={{ width: '30%' }} />
            </div>
            {/* status row */}
            <div className={styles['skeleton-status-row']}>
              <div className={styles['skeleton-status-chip']} />
              <div className={styles['skeleton-status-chip']} style={{ width: '4rem' }} />
            </div>
          </div>
        </div>

        {/* Tabs skeleton */}
        <div className={styles['skeleton-tabs-wrap']}>
          <div className={styles['skeleton-tab']} />
          <div className={styles['skeleton-tab']} />
          <div className={styles['skeleton-tab']} />
        </div>

        {/* Photo grid skeleton */}
        <PhotoGrid>
          {Array.from({ length: 24 }).map((_, i) => <PhotoCardSkeleton key={i} />)}
        </PhotoGrid>
      </div>
    );
  }

  // ── Main view ─────────────────────────────────────────────────────────────

  return (
    <div className={styles['page']}>
      <div className={styles['page-header-constrained']}>
      {/* Header Container */}
      <motion.div
        className={[styles['header-container'], coverPhotoUrl ? styles['has-cover'] : ''].filter(Boolean).join(' ')}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
      >
        {coverPhotoUrl && (
          <div className={styles['header-bg-clip']} aria-hidden>
            {/* Background image on its own element so filter: blur is correctly
                clipped by the clip wrapper's overflow: hidden + border-radius */}
            <div
              className={styles['header-bg']}
              style={{ backgroundImage: `url(${coverPhotoUrl})` }}
            />
            {/* Dark tint overlay — separate from the blur so opacity is independent */}
            <div className={styles['header-overlay']} />
          </div>
        )}

        {/* Top actions row */}
        <div className={styles['header-actions-row']}>
          <motion.button
            onClick={() => navigate('/albums')}
            className={styles['action-icon-btn']}
            whileTap={{ scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            aria-label="Back to albums"
          >
            <ArrowLeft className={styles['icon-sm']} />
          </motion.button>

          <div className={styles['header-actions-right']}>
            <GooglePhotosSyncButton
              albumId={albumId!}
              totalAlbumPhotos={totalPhotoCount}
              faceEnabled={faceEnabled}
              hasFacePhoto={hasFacePhoto}
              myPhotoIds={myPhotoIds}
              restrictedView={restrictedView}
              restrictedAllPhotoIds={restrictedAllPhotoIds}
              externalJobTrigger={gpSyncJobTrigger}
              autoOpenOptions={gpAutoOpenOptions}
              onJobChange={setGpSyncJob}
              syncedPhotoIds={gpSyncedPhotoIds}
              onSyncStarted={handleGpSyncStarted}
              onEnterSelectMode={(filter) => {
                setGpSyncSelectFilter(filter);
                setSelectMode(true);
                if (filter === 'my_photos') setActiveTab('my-photos');
              }}
            />

            <div className={styles['header-menu-wrap']} ref={moreRef}>
              <motion.button
                ref={moreTriggerRef}
                onClick={() => setMoreOpen((o) => !o)}
                className={styles['action-icon-btn']}
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                aria-label="Album menu"
              >
                <MoreHorizontal className={styles['icon-sm']} />
              </motion.button>
            </div>
          </div>
        </div>

        {/* Content: Title, Album Description, Event Name, Status */}
        <div className={styles['header-info']}>
          <h1 className={styles['page-title']}>
            {album?.name}
          </h1>

          {hasDescription && (
            <div className={styles['desc-wrap']}>
              <p ref={descriptionRef} className={descExpanded ? styles['desc-text'] : styles['desc-text-clamped']}>
                {descriptionText}
              </p>
              {isDescriptionTruncated && (
                <button
                  onClick={() => setDescExpanded((o) => !o)}
                  className={styles['show-more-btn']}
                >
                  {descExpanded ? 'Show less' : 'Show more'}
                  <motion.span
                    animate={{ rotate: descExpanded ? 180 : 0 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                    className={styles['chevron-icon']}
                  >
                    <ChevronDown className={styles['chevron-icon']} />
                  </motion.span>
                </button>
              )}
            </div>
          )}

          {event?.name && (
            <div className={styles['event-name-wrap']}>
              <span className={styles['event-label']}>Event:</span>
              <span className={styles['event-name']}>{event.name}</span>
            </div>
          )}

          <div className={styles['status-row']}>
            {album?.isLive && (typeof album.eventId === 'string' || album.eventId?.status !== 'ended') && <LiveBadge active />}
            
            <span className={styles['photo-count']}>
              {totalPhotoCount} photo{totalPhotoCount !== 1 ? 's' : ''}
            </span>

            {gpSyncJob && (gpSyncJob.status === 'pending' || gpSyncJob.status === 'running') && (
              <span className={styles['gp-sync-status']} data-state="syncing">
                <img
                  src="https://d3ic3ju44zc444.cloudfront.net/assets/LogoImages/google-photos-6.svg"
                  width={12} height={12} alt=""
                />
                {gpSyncJob.totalPhotos > 0
                  ? `Syncing ${gpSyncJob.syncedPhotos}/${gpSyncJob.totalPhotos}`
                  : 'Starting sync…'}
              </span>
            )}
            {gpSyncJob?.status === 'completed' && (
              <span className={styles['gp-sync-status']} data-state="done">
                <img
                  src="https://d3ic3ju44zc444.cloudfront.net/assets/LogoImages/google-photos-6.svg"
                  width={12} height={12} alt=""
                />
                {gpSyncJob.syncedPhotos} synced
              </span>
            )}
            {gpSyncJob?.status === 'failed' && (
              <span className={styles['gp-sync-status']} data-state="failed">
                Sync failed
              </span>
            )}
          </div>
        </div>
      </motion.div>
      </div>{/* end page-header-constrained */}

      {/* Album tabs — face + Google Photos */}
      {!state.loading && albumTabs.length > 1 && (
        <div className={styles['face-tabs-wrap']} data-testid="face-tabs">
          <TabBar
            tabs={albumTabs}
            activeTab={effectiveTab}
            onChange={handleTabChange}
            layoutId="album-face-tab"
            variant="underline"
          />
        </div>
      )}

      {/* Photo grid with download button overlay */}
      <div className={styles['photo-grid-wrapper']}>
        <AnimatePresence mode="wait" custom={tabDirectionRef.current}>
        <motion.div
          key={effectiveTab}
          custom={tabDirectionRef.current}
          variants={tabSlideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={tabSlideTransition}
          style={{ willChange: 'transform, opacity' }}
        >
        {/* "Are these you?" review strip — borderline candidates the viewer can
            confirm/reject. Shows above the grid on the My Photos tab (even when
            no confident matches exist yet), never during the initial load. */}
        {showMyPhotos && hasFacePhoto && !myPhotosLoading && reviewPhotos.length > 0 && (
          <MyPhotosReviewStrip
            albumId={albumId!}
            photos={reviewPhotos}
            onConfirmed={handleReviewConfirmed}
            onRejected={handleReviewRejected}
          />
        )}
        {(() => {
        // My Photos tab — loading state
        if (showMyPhotos && myPhotosLoading) {
          return (
            <div className={styles['empty-album']}>
              <div className={styles['empty-state-content-col']}>
                <ScanFace size={48} className={styles['empty-state-icon']} />
                <p className={styles['empty-album-text']}>Finding your photos...</p>
                <p className={styles['empty-state-subtitle']}>
                  We're scanning the album for photos you appear in. This may take a moment.
                </p>
                <div className={styles['loading-dots-wrap']}>
                  {[0, 1, 2].map((idx) => (
                    <motion.div
                      key={idx}
                      className={styles['loading-dot-item']}
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ repeat: Infinity, duration: 1.2, delay: idx * 0.2 }}
                    />
                  ))}
                </div>
              </div>
            </div>
          );
        }

        // My Photos tab — error state
        if (showMyPhotos && myPhotosError) {
          return (
            <div className={styles['empty-album']}>
              <div className={styles['empty-state-content-row']}>
                <MachhaCharacter state="Error" size={220} />
                <div className={styles['empty-state-error-col']}>
                  <p className={styles['empty-album-text-left']}>Something went wrong</p>
                  <p className={styles['empty-state-subtitle-left']}>
                    {myPhotosError}
                  </p>
                  <Button onClick={() => fetchMyPhotos(true)}>
                    Try Again
                  </Button>
                </div>
              </div>
            </div>
          );
        }

        // My Photos tab — needs selfie (and hasn't just uploaded one)
        if (showMyPhotos && !hasFacePhoto && !selfieJustUploaded) {
          return (
            <div className={styles['empty-album']}>
              <div className={styles['empty-state-content-col']}>
                <Camera size={48} className={styles['empty-state-icon']} />
                <p className={styles['empty-album-text']}>Find your photos!</p>
                <p className={styles['empty-state-subtitle']}>
                  Take a quick selfie and we'll find every photo you appear in. Your selfie is kept private and only used for matching.
                </p>
                <Button onClick={() => setSelfiePromptOpen(true)}>
                  <Camera size={16} />
                  Take Selfie
                </Button>
              </div>
            </div>
          );
        }

        // Google Photos tab — fetching synced photo objects
        if (showGpSynced && gpSyncedPhotosLoading) {
          return (
            <div className={styles['empty-album']}>
              <div className={styles['empty-state-content-col']}>
                <img
                  src="https://d3ic3ju44zc444.cloudfront.net/assets/LogoImages/google-photos-6.svg"
                  width={52} height={52} alt="Google Photos"
                  style={{ opacity: 0.4 }}
                  draggable={false}
                />
                <p className={styles['empty-album-text']}>Loading synced photos…</p>
              </div>
            </div>
          );
        }

        // Google Photos tab — not yet connected (or still checking)
        if (showGpSynced && (gpConnectChecking || gpConnected === false)) {
          return (
            <div className={styles['empty-album']}>
              <div className={styles['empty-state-content-col']}>
                <img
                  src="https://d3ic3ju44zc444.cloudfront.net/assets/LogoImages/google-photos-6.svg"
                  width={52}
                  height={52}
                  alt="Google Photos"
                  style={{ opacity: gpConnectChecking ? 0.4 : 1, transition: 'opacity 200ms ease' }}
                  draggable={false}
                />
                {gpConnectChecking ? (
                  <p className={styles['empty-album-text']}>Checking connection…</p>
                ) : (
                  <>
                    <p className={styles['empty-album-text']}>Connect Google Photos</p>
                    <p className={styles['empty-state-subtitle']}>
                      Sync this album's photos to your Google Photos library. Connect once and sync whenever you like.
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => { window.location.href = googlePhotosApi.connectUrl(albumId!); }}
                    >
                      <Cloud size={15} />
                      Connect Google Photos
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        }

        // Google Photos tab — connected but nothing synced yet
        if (showGpSynced && gpConnected === true && displayPhotos.length === 0) {
          return (
            <div className={styles['empty-album']}>
              <div className={styles['empty-state-content-col']}>
                <MachhaCharacter state="Empty" size={160} />
                <p className={styles['empty-album-text']}>No synced photos yet</p>
                <p className={styles['empty-state-subtitle']}>
                  Use the sync button above to send photos from this album to your Google Photos library.
                </p>
              </div>
            </div>
          );
        }

        // My Photos and Highlights both filter down from `photos`, which may still
        // be paginating in the pages that contain those ids — show a skeleton
        // instead of flashing the terminal empty state while that fetch is in flight.
        if (
          displayPhotos.length === 0 &&
          loadingMore &&
          (showMyPhotos || showHighlights)
        ) {
          return (
            <PhotoGrid>
              {Array.from({ length: 12 }).map((_, i) => <PhotoCardSkeleton key={i} />)}
            </PhotoGrid>
          );
        }

        // The Highlights tab also needs the highlight ids themselves — before the
        // getMyPhotos fetch resolves, `viewerHighlightIds` is empty, which would
        // otherwise look identical to a genuinely-empty album.
        if (
          displayPhotos.length === 0 &&
          showHighlights &&
          (!myPhotosLoaded || myPhotosLoading)
        ) {
          return (
            <PhotoGrid>
              {Array.from({ length: 12 }).map((_, i) => <PhotoCardSkeleton key={i} />)}
            </PhotoGrid>
          );
        }

        return displayPhotos.length === 0 ? (
        <div className={styles['empty-album']}>
          <MachhaCharacter state="Empty" size={160} />
          <p className={styles['empty-album-text']}>
            {showMyPhotos
              ? 'No photos of you found yet.'
              : showHighlights
                ? 'No highlights yet.'
                : album?.isLive ? 'Waiting for photos...' : 'No photos in this album yet.'}
          </p>
          {showHighlights && !hasFacePhoto && (
            <Button onClick={() => setSelfiePromptOpen(true)} style={{ marginTop: 12 }}>
              <Camera size={16} />
              Take a selfie to find your photos
            </Button>
          )}
          {album?.isLive && showAllPhotos && (
            <div className={styles['live-hint']}>
              <motion.span
                className={styles['live-dot']}
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
              />
              <p className={styles['live-hint-text']}>
                Photos will appear here in real-time as cameras upload
              </p>
            </div>
          )}
          {showMyPhotos && album?.isLive && (
            <p className={styles['live-auto-match-hint']}>
              New photos are matched automatically — check back soon!
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Virtual grid container — only visible rows are in the DOM. */}
          <div
            ref={gridContainerRef}
            data-testid="photo-grid"
            className={styles['virtual-grid-wrap']}
            style={{ height: virtualizer.getTotalSize() }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const isSkeletonRow = virtualRow.index === photoRows.length;
              const row = isSkeletonRow ? null : photoRows[virtualRow.index];

              return (
                <div
                  key={virtualRow.key}
                  className={styles['virtual-row-container']}
                  style={{
                    // Subtract the bottom inter-row gap from the last content row
                    // so the container height matches the actual rendered content.
                    height: isSkeletonRow
                      ? virtualRow.size
                      : virtualRow.size - gridGap,
                    transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                    gridTemplateColumns: `repeat(${gridCols}, ${cellSize}px)`,
                    justifyContent: 'start',
                    gap: `${gridGap}px`,
                  }}
                >
                  {isSkeletonRow
                    ? Array.from({ length: gridCols }).map((_, i) => (
                        <PhotoCardSkeleton key={i} />
                      ))
                    : row!.map((photo) => (
                        <motion.div
                          key={photo._id}
                          layoutId={photo._id}
                          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                          style={{ zIndex: photoMeta[photo._id]?.isPinned ? 10 : 1 }}
                        >
                          <AlbumPhotoCell
                            photo={photo}
                            photoIndex={photoIndexMap.get(photo._id) ?? 0}
                            isSelected={selectedIds.has(photo._id)}
                            selectMode={selectMode}
                            onSelect={toggleSelect}
                            onView={handleView}
                            likeCount={photoMeta[photo._id]?.likeCount}
                            isLikedByMe={photoMeta[photo._id]?.isLikedByMe}
                            onToggleLike={handleToggleLike}
                            onLikeCountTap={handleLikeCountTap}
                            isPinned={photoMeta[photo._id]?.isPinned}
                            onTogglePin={pinHandler}
                            onReport={handleReport}
                            onShare={handleShare}
                            useClientCompositor={liveOverlayCompositor}
                            overlayConfigs={album?.overlayConfigs}
                            isSyncedToGooglePhotos={gpSyncedPhotoIds.has(photo._id)}
                            isGpSyncFailed={gpFailedPhotoIds.has(photo._id)}
                            isGpSyncRetrying={gpRetryingPhotoIds.has(photo._id)}
                            onRetryGpSync={handleRetryGpSync}
                            onBadgeClick={handleBadgeClick}
                            onNotMe={showMyPhotos ? handleNotMe : undefined}
                          />
                        </motion.div>
                      ))
                  }
                </div>
              );
            })}
          </div>

          {hasMore && <div ref={sentinelCallbackRef} className={styles['sentinel']} />}
        </>
      );
      })()}
        </motion.div>
        </AnimatePresence>
      </div>

      {/* Sticky action buttons bar — appears at bottom for batch operations & download */}
      <AnimatePresence>
        {!selectMode && displayPhotos.length > 0 && (
          <motion.div
            className={styles['sticky-select-bar']}
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            exit={{ y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          >
            <motion.button
              onClick={handleDownloadAll}
              disabled={downloadingAll}
              className={styles['download-btn-sticky']}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <Download className={styles['icon-action']} />
              {/* With the client-side archiver there is no server build to wait
                  on, so the label no longer depends on zip state — it just says
                  what the button does for the tab you're on. */}
              {downloadingAll ? 'Preparing…'
                : showMyPhotos ? 'Download My Photos'
                : showHighlights ? 'Download Highlights'
                : 'Download all'}
            </motion.button>
            <motion.button
              onClick={() => setSelectMode(true)}
              className={styles['select-btn-sticky']}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <SquareMousePointer className={styles['icon-action']} />
              Select Photos
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selfie prompt modal */}
      <SelfiePrompt
        isOpen={selfiePromptOpen}
        onClose={() => setSelfiePromptOpen(false)}
        onCaptured={handleSelfieCaptured}
        // Optional: even a restricted viewer can browse highlights in the All
        // Photos tab without a selfie — a selfie only adds their own matches.
        required={false}
      />

      {/* Fullscreen viewer */}
      {viewerIndex !== null && !selectMode && (
        <AlbumPhotoViewer
          photos={displayPhotos}
          initialIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onShare={(photoId) => setSharePhotoId(photoId)}
          onReport={(photoId) => setReportPhotoId(photoId)}
          onToggleLike={(photoId) => handleToggleLike(photoId, { stopPropagation: () => {} } as unknown as React.MouseEvent)}
          onNotMe={showMyPhotos ? handleNotMe : undefined}
          photoMeta={photoMeta}
          albumName={album?.name}
          eventName={event?.name}
          useClientCompositor={liveOverlayCompositor}
          overlayConfigs={album?.overlayConfigs}
        />
      )}

      {/* Batch action bar */}
      <AnimatePresence>
        {selectMode && (
          <AlbumBatchActionBar
            selectedCount={selectedIds.size}
            totalCount={displayPhotos.length}
            onDownload={handleBatchDownload}
            onSelectAll={handleSelectAll}
            onDeselectAll={() => setSelectedIds(new Set())}
            selectingAll={selectingAll}
            onExit={exitSelectMode}
            downloading={batchDownloading}
            onSyncToGooglePhotos={handleGpSyncSelected}
            syncingToGooglePhotos={gpSyncingSelected}
          />
        )}
      </AnimatePresence>

      {/* Leave confirmation modal */}
      <Modal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        title="Leave album?"
        size="sm"
      >
        <div className={styles['modal-body']}>
          <p className={styles['modal-text']}>
            You will lose access to this album. You can rejoin using the invite link if you have it.
          </p>
          <div className={styles['modal-actions']}>
            <Button variant="ghost" onClick={() => setShowLeaveModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleLeave} disabled={leaving}>
              {leaving ? 'Leaving…' : 'Leave Album'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Notification opt-in modal — shown when toggling on without a phone number */}
      {albumId && album && (
        <NotificationOptInModal
          albumId={albumId}
          albumName={album.name}
          faceRecognitionEnabled={album.faceRecognitionEnabled ?? false}
          isOpen={notifModalOpen && Date.now() >= PHONE_OPT_IN_DISABLED_UNTIL}
          onDone={() => setNotifModalOpen(false)}
          onEnabled={() => setNotificationsEnabled(true)}
          context={notifModalContext}
        />
      )}

      {/* Like privacy prompt — shown on first-ever like */}
      <LikePrivacyPrompt
        isOpen={privacyPromptOpen}
        onClose={() => {
          setPrivacyPromptOpen(false);
          pendingLikeRef.current = null;
        }}
        onChoice={handlePrivacyChoice}
      />

      {/* Photo report sheet */}
      {albumId && (
        <PhotoReportSheet
          isOpen={reportPhotoId !== null}
          onClose={() => setReportPhotoId(null)}
          albumId={albumId}
          photoId={reportPhotoId ?? ''}
          onReported={() => setReportPhotoId(null)}
        />
      )}

      {/* Live slideshow */}
      <LiveSlideshowModal
        isOpen={slideshowOpen}
        onClose={() => setSlideshowOpen(false)}
        photos={photos}
        albumName={album?.name ?? 'Album'}
      />

      {/* Venue Display setup modal */}
      <SlideshowSetupModal
        album={
          slideshowSetupOpen && album
            ? ({
                ...album,
                eventId: typeof album.eventId === 'string' ? album.eventId : album.eventId._id,
              } as Album)
            : null
        }
        isOpen={slideshowSetupOpen}
        onClose={() => setSlideshowSetupOpen(false)}
        onUpdated={handleAlbumSlideshowUpdated}
      />

      {/* Social share sheet */}
      {(() => {
        const sharePhoto = sharePhotoId ? photos.find((p) => p._id === sharePhotoId) : null;
        const shareUrl = sharePhoto?.processedUrl ?? sharePhoto?.originalUrl ?? sharePhoto?.thumbnailUrl ?? '';
        return (
          <ShareSheet
            isOpen={sharePhotoId !== null && !!shareUrl}
            onClose={() => setSharePhotoId(null)}
            photoUrl={shareUrl}
            albumName={album?.name ?? 'Album'}
            eventName={event?.name ?? ''}
          />
        );
      })()}

      {/* Likers modal — shows who liked a photo */}
      {albumId && (
        <LikersModal
          isOpen={likersPhotoId !== null}
          onClose={() => setLikersPhotoId(null)}
          albumId={albumId}
          photoId={likersPhotoId ?? ''}
        />
      )}

      {/* Creator Controls modal — stats + slideshow tools, creator-only */}
      {album && (
        <CreatorControlsModal
          isOpen={creatorControlsOpen}
          onClose={() => setCreatorControlsOpen(false)}
          album={album}
          isLive={isLive}
          faceEnabled={faceEnabled}
          eventId={typeof album.eventId === 'string' ? album.eventId : album.eventId._id}
          totalPhotoCount={totalPhotoCount}
          photoCount={photos.length}
          activityStats={activityStats}
          onPreviewSlideshow={() => setSlideshowOpen(true)}
          onSlideshowSetup={() => setSlideshowSetupOpen(true)}
        />
      )}

      {/* Public share link modal — creator-only */}
      {isCreator && albumId && (
        <SharePublicLinkModal
          isOpen={sharePublicOpen}
          onClose={() => setSharePublicOpen(false)}
          albumId={albumId}
        />
      )}

      {/* More menu dropdown — portaled to document.body so it escapes the
          motion.div's transform-induced stacking context and overflow clipping */}
      {createPortal(
        <AnimatePresence>
          {moreOpen && (
            <motion.div
              ref={moreDropdownRef}
              className={styles['header-menu-dropdown']}
              style={{ top: moreDropdownPos.top, right: moreDropdownPos.right }}
              initial={{ opacity: 0, y: -4, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            >
              {isLive && isCreator && (
                <button
                  onClick={() => { setMoreOpen(false); setCreatorControlsOpen(true); }}
                  className={styles['menu-item']}
                >
                  <Sparkles className={styles['icon-action']} /> Creator Controls
                </button>
              )}
              {album?.inviteToken && (
                <button
                  onClick={() => { setMoreOpen(false); handleCopyInviteLink(); }}
                  className={styles['menu-item']}
                >
                  <Link2 className={styles['icon-action']} /> Copy Invite Link
                </button>
              )}
              <button
                onClick={handleToggleNotifications}
                className={styles['menu-item']}
              >
                {notificationsEnabled
                  ? <><BellOff className={styles['icon-action']} /> Turn off notifications</>
                  : <><Bell className={styles['icon-action']} /> Turn on notifications</>
                }
              </button>
              <button
                onClick={() => { setMoreOpen(false); setShowLeaveModal(true); }}
                className={styles['menu-item-danger']}
              >
                <LogOut className={styles['icon-action']} /> Leave Album
              </button>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

    </div>
  );
}
